// One specialist's status row, section by section: the project's specialist tab
// and the specialist's Portfolio page both render a row through this, so a
// project reads the same on either surface (principle 23).
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

import type { PortfolioRow } from "../api/hooks/useAgentPortfolio";
import {
  Applications,
  ConnectionChain,
  OptionsOnTheTable,
  StatusForCard,
  VerdictReason,
  chainNodes,
  resolveVerdictItem,
} from "./PortfolioRowParts";
import { type SpecialistLists, StatusCardChips } from "./StatusCardParts";
import { Fold } from "./Fold";
import styles from "./SpecialistRowSections.module.css";

export const SectionCard = ({
  title,
  aside,
  asideAt = "end",
  id,
  folded,
  children,
}: {
  title: string;
  // Rendered in the header; null for none.
  aside: ReactNode;
  // At the right of the header, or straight after the title (the Status card's chips).
  asideAt?: "start" | "end";
  // For a link to land on the card.
  id?: string;
  // Given, the card opens and closes from its header, open on first render where true.
  folded?: { open: boolean };
  children: ReactNode;
}) => {
  const head = (
    <>
      <h3>{title}</h3>
      {aside !== null && (
        <>
          {asideAt === "end" && <span className={styles.spacer} />}
          {aside}
        </>
      )}
    </>
  );
  return (
    <section className={styles.card} id={id}>
      {folded ? (
        <Fold head={head} headClassName={styles.cardHead} open={folded.open}>
          {children}
        </Fold>
      ) : (
        <>
          <header className={styles.cardHead}>{head}</header>
          {children}
        </>
      )}
    </section>
  );
};

export const SectionBlock = ({ children }: { children: ReactNode }) => (
  <div className={styles.block}>{children}</div>
);

export const SpecialistRowSections = ({
  row,
  keepsApplications,
  lists,
  decisions = [],
  readyToBuild,
  belowReadyToBuild,
  history,
  children,
}: {
  row: PortfolioRow;
  /** Whether this agent's row keeps an Applications box (Grid's does). */
  keepsApplications: boolean;
  /** Its actions on the project, for the owner chip; null while they load. */
  lists: SpecialistLists | null;
  /** The open decision actions, linked under the options. Nothing ties one option to
   * an action, so the link is for the options as a whole. */
  decisions?: { title: string; href: string }[];
  /** The Ready to build card, first in the right column; null while the lists load. */
  readyToBuild: ReactNode;
  /** Cards straight under Ready to build: Legal's Contracts and Planning. */
  belowReadyToBuild?: ReactNode;
  /** The History card, last in the right column. */
  history?: ReactNode;
  /** Cards below Status in the left column: Next actions and Tracked dates. */
  children?: ReactNode;
}) => (
  // Two columns that stack independently, so a tall card in one never pushes the
  // other column's cards down.
  <div className={styles.fit}>
    <div className={styles.grid2}>
      <div className={styles.stack}>
        <SectionCard
          title="Status"
          aside={<StatusCardChips row={row} lists={lists} />}
          asideAt="start"
        >
          {row.on_track?.verdict && (
            <SectionBlock>
              <VerdictReason
                onTrack={row.on_track}
                found={resolveVerdictItem(row.on_track, lists)}
              />
            </SectionBlock>
          )}
          <SectionBlock>
            <StatusForCard text={row.status_details} omitLabels={[]} />
          </SectionBlock>
        </SectionCard>
        {children}
      </div>

      <div className={styles.stack}>
        {readyToBuild}
        {belowReadyToBuild}
        {keepsApplications && (
          <SectionCard title="Applications" aside={null}>
            <SectionBlock>
              <Applications applications={row.applications} />
            </SectionBlock>
          </SectionCard>
        )}
        <SectionCard title="Options on the table" aside={null}>
          <SectionBlock>
            <OptionsOnTheTable text={row.options} />
            {decisions.length > 0 && (
              <p className={styles.decisionLink}>
                {decisions.length === 1
                  ? "Open decision on these options: "
                  : "Open decisions on these options: "}
                {decisions.map((d, i) => (
                  <span key={d.href}>
                    {i > 0 && "; "}
                    <Link to={d.href}>{d.title}</Link>
                  </span>
                ))}
              </p>
            )}
          </SectionBlock>
        </SectionCard>

        <SectionCard
          title="Parties"
          aside={
            <span className={styles.meta}>
              {chainNodes(row.counterparties).length} on the roster
            </span>
          }
        >
          <SectionBlock>
            <ConnectionChain counterparties={row.counterparties} />
          </SectionBlock>
        </SectionCard>
        {history}
      </div>
    </div>
  </div>
);
